/* c8 ignore file */
/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import {
  AssignmentStrategyDTO,
  ConsultationRequestStatusDTO,
  FileKindEnum,
  GenderDTO,
  type MedicalFieldDTO,
  type OrganSupportDTO,
  ScheduledConsultationTypeDTO,
  ScopeBoundaryDTO,
  ShiftTypeDTO,
  UrgencyDTO,
  UserRightsEnum
} from '@/generated'
import { useMedicalFieldsStore } from '@/stores/medicalFields'
import { useUserStore } from '@/stores/user'

export const translateConsultationRequestStatusDTO = (
  status: ConsultationRequestStatusDTO | string,
  t: Function
): string => {
  switch (status) {
    case ConsultationRequestStatusDTO.CREATED:
      return t('Created')
    case ConsultationRequestStatusDTO.ACCEPTED:
      return t('Accepted')
    case ConsultationRequestStatusDTO.IN_PROGRESS:
      return t('In progress')
    case ConsultationRequestStatusDTO.REPORT_PENDING:
      return t('Consultation report pending')
    case ConsultationRequestStatusDTO.COMPLETED:
      return t('Completed')
    case ConsultationRequestStatusDTO.DELETED:
      return t('Deleted')
    default:
      return status
  }
}

const hasOwn = (obj: object, key: string): boolean => Object.prototype.hasOwnProperty.call(obj, key)

const ownTranslation = (
  value: Record<string, string> | undefined,
  language: string | undefined
): string | undefined =>
  language && value && hasOwn(value, language) ? value[language] : undefined

const isMedicalFieldDTO = (value: unknown): value is MedicalFieldDTO =>
  typeof value === 'object' && value !== null && 'id' in value && 'names' in value

export function translateMedicalField(id: string): string | undefined {
  const language = useUserStore().user?.language
  return ownTranslation(useMedicalFieldsStore().medicalFieldsById.get(id)?.names, language)
}

function translateMedicalFieldDTO(field: MedicalFieldDTO): string {
  const language = useUserStore().user?.language
  return ownTranslation(field.names, language) || translateMedicalField(field.id) || field.id
}

export const translateMedicalFieldsDTO = (field: MedicalFieldDTO | string): string => {
  if (isMedicalFieldDTO(field)) {
    return translateMedicalFieldDTO(field)
  }
  return translateMedicalField(field) || field
}

export const translateMedicalFieldDTOs = (fields: string[]): string => {
  if (!fields || fields.length === 0) {
    return ''
  }

  const translatedFields = fields.map((field) => translateMedicalFieldsDTO(field))

  return translatedFields.join(', ')
}

export const translateShiftTypeDTO = (
  shiftType: ShiftTypeDTO | string,
  t: Function,
  shortLabel: boolean = false
): string => {
  switch (shiftType) {
    case ShiftTypeDTO.FIRST_SHIFT:
      return shortLabel ? t('1st shift') : t('First Shift')
    case ShiftTypeDTO.BACKUP_SHIFT:
      return shortLabel ? t('2nd shift') : t('Backup Shift')
    case ShiftTypeDTO.NO_SHIFT:
      return t('No Shift')
    case ShiftTypeDTO.GOD_MODE:
      return t('Routing and Patient Viewer')
    case ShiftTypeDTO.CHIEF_PHYSICIAN:
      return t('Routing and Patient Supervisor')
    default:
      return shiftType
  }
}

export const translateUserRightsEnum = (
  userRights: UserRightsEnum | string,
  t: Function
): string => {
  switch (userRights) {
    case UserRightsEnum.GOD_MODE:
      return t('Routing and Patient Viewer')
    case UserRightsEnum.CHIEF_PHYSICIAN:
      return t('Routing and Patient Supervisor')
    case UserRightsEnum.HOSPITAL_IT:
      return t('Hospital IT')
    case UserRightsEnum.TCC_IT:
      return t('TCC IT')
    case UserRightsEnum.TCC_PHYSICIAN:
      return t('TCC Physician')
    case UserRightsEnum.HOSPITAL_PHYSICIAN:
      return t('Hospital Physician')
    case UserRightsEnum.EASY_RADIOLOGY:
      return t('Easy Radiology')
    default:
      return userRights
  }
}

export const translateFileKindEnum = (fileKind: FileKindEnum | string, t: Function): string => {
  switch (fileKind) {
    case FileKindEnum.MEDICAL_REPORT:
      return t('Medical Reports')
    case FileKindEnum.CONSULTATION_REPORT:
      return t('Consultation Reports')
    case FileKindEnum.SCHEDULED_CONSULTATION_REPORT:
      return t('Scheduled Consultation Reports')
    case FileKindEnum.DOCUMENTS:
      return t('Documents')
    default:
      return fileKind
  }
}

export const translateUrgencyDTO = (
  urgency: UrgencyDTO,
  t: Function,
  shortLabel: boolean = false
): string => {
  switch (urgency) {
    case UrgencyDTO.EMERGENCY:
      return shortLabel ? t('Emergency') : t('Emergency Call')
    case UrgencyDTO.URGENT:
      return t('Urgent')
    case UrgencyDTO.ROUTINE:
      return t('Routine')
    case UrgencyDTO.FOLLOW_UP:
      return t('Follow Up')
    default:
      return urgency
  }
}

export const translateFullGenderDTO = (gender: GenderDTO, t: Function) => {
  switch (gender) {
    case GenderDTO.MALE:
      return t('Male')
    case GenderDTO.FEMALE:
      return t('Female')
    case GenderDTO.VARIOUS:
      return t('Other')
    case GenderDTO.NOT_DEFINED:
      return t('Not defined')
    default:
      return t('Unknown')
  }
}

export const translateGenderDTO = (gender: GenderDTO | string, t: Function): string => {
  switch (gender) {
    case GenderDTO.MALE:
      return t('m')
    case GenderDTO.FEMALE:
      return t('f')
    case GenderDTO.VARIOUS:
      return t('v')
    case GenderDTO.NOT_DEFINED:
      return t('n.d.')
    default:
      return gender
  }
}

export const translateScheduledConsultationTypeDTO = (
  type: ScheduledConsultationTypeDTO | string,
  t: (key: string) => string
): string => {
  switch (type) {
    // [UI-Only] Map `PATIENT_VISIT` to "Ward Round" per client request.
    // The backend DTO value remains unchanged to avoid breaking changes
    // during the current environment stabilization phase.
    case ScheduledConsultationTypeDTO.PATIENT_VISIT:
      return t('Ward Round')
    case ScheduledConsultationTypeDTO.CONFERENCE:
      return t('Case Conference')
    case ScheduledConsultationTypeDTO.PATIENT_PRESENTATION:
      return t('Patient Presentation')
    default:
      return type
  }
}

export const translateOrganSupportDTO = (
  organSupportType: keyof OrganSupportDTO,
  t: (key: string) => string
): string => {
  switch (organSupportType) {
    case 'dialysis':
      return t('Dialysis')
    case 'vasopressors':
      return t('Vasopressors')
    case 'ventilation':
      return t('Ventilation')
    case 'ecmo':
      return t('ECMO')
    case 'impella':
      return t('Impella')
    default:
      return organSupportType
  }
}

export const translateConsultationStatusLog = (
  status: ConsultationRequestStatusDTO,
  t: (key: string) => string
): string => {
  switch (status) {
    case ConsultationRequestStatusDTO.CREATED: {
      return t('In escalation')
    }
    case ConsultationRequestStatusDTO.ACCEPTED: {
      return t('Assigned')
    }
    case ConsultationRequestStatusDTO.IN_PROGRESS: {
      return t('In progress')
    }
    case ConsultationRequestStatusDTO.REPORT_PENDING: {
      return t('Consultation report pending')
    }
    case ConsultationRequestStatusDTO.COMPLETED: {
      return t('Completed')
    }
    case ConsultationRequestStatusDTO.DELETED: {
      return t('Deleted')
    }
    default:
      return status
  }
}

export const translateAssignmentStrategyDTO = (
  strategy: AssignmentStrategyDTO,
  t: (key: string) => string
): string => {
  switch (strategy) {
    case AssignmentStrategyDTO.SINGLE: {
      return t('Single Routing')
    }
    case AssignmentStrategyDTO.BROADCAST: {
      return t('Broadcast')
    }
    default:
      return strategy
  }
}

export const translateScopeBoundaryDTO = (
  scopeBoundary: ScopeBoundaryDTO,
  t: (key: string) => string
): string => {
  switch (scopeBoundary) {
    case ScopeBoundaryDTO.CLIENT:
      return t('All in Network')
    case ScopeBoundaryDTO.HOSPITAL:
      return t('Within Hospital')
    case ScopeBoundaryDTO.DEPARTMENT:
      return t('Within Department')
    case ScopeBoundaryDTO.WARD:
      return t('Within Ward')
    case ScopeBoundaryDTO.HOSPITAL_HUB:
      return t('Selected Hospitals')
    default:
      return scopeBoundary
  }
}
